import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import { useInView } from '@/hooks/useInView'
import Header from '@/components/layout/Header'
import Hero from '@/components/sections/Hero'
import Process from '@/components/sections/Process'
import Services from '@/components/sections/Services'
import About from '@/components/sections/About'
import Contact from '@/components/sections/Contact'
import Footer from '@/components/layout/Footer'
import SEOHead from '@/components/SEO/SEOHead'
import { resolveSeo } from '@/seo/resolveSeo'
import { buildHomeJsonLd } from '@/seo/jsonLd'
import FloatingAIChat from '@/components/ui/FloatingAIChat'
import { PARTNERS } from '@/lib/constants'
import { fetchHomeCmsContent } from '@/api/cmsService'
import { cmsDebug, cmsListOrFallback, cmsTextOrFallback } from '@/api/helpers'
import { cmsClamp, cmsTextTitle } from '@/styles/cmsText'
import type { HomeCmsContent } from '@/api/types'

/**
 * Temporary production kill-switch for the AI chat widget.
 * Set VITE_ENABLE_AI_CHAT=true in env (or flip this to true) to show it again.
 */
const ENABLE_AI_CHAT = import.meta.env.VITE_ENABLE_AI_CHAT === 'true'

const BUSINESS_TITLE_FALLBACK = 'Your Business, Under Your Control'
const BUSINESS_DESC_FALLBACK =
  'We understand the challenges facing growing businesses in the MENA region. You need technology that solves problems, not creates them. We built DIGITREK to be a different kind of digital partner - one that prioritizes clarity, flexibility, and real results.'

export default function HomePage() {
  // Detect mobile on initial load to prevent flickering
  const detectMobile = () => {
    const isMobileWidth = window.innerWidth < 768
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return isMobileWidth || isMobileUA
  }

  const [scale, setScale] = useState(1)
  const [isMobile, setIsMobile] = useState(detectMobile())
  const [cmsData, setCmsData] = useState<HomeCmsContent | null>(null)
  const [cmsLoading, setCmsLoading] = useState(true)
  const [cmsError, setCmsError] = useState<string | null>(null)
  const [mobileCardHeight, setMobileCardHeight] = useState<number | undefined>(undefined)
  const mobileCardsContainerRef = useRef<HTMLDivElement>(null)

  const businessRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const [contentHeight, setContentHeight] = useState(0)
  const { hash } = useLocation()

  useEffect(() => {
    const loadContent = async () => {
      try {
        setCmsLoading(true)
        const data = await fetchHomeCmsContent()
        cmsDebug('HomePage merged CMS', {
          businessTitle: data.business.title,
          serviceSectionTitle: data.services.title,
          heroTitle: data.hero.title,
          clearPathSteps: data.clearPath.steps.length,
        })
        setCmsData(data)
        if (!data.hero.title?.trim() && data.heroCmsHttpStatus === 403) {
          setCmsError(
            'Hero title unavailable: enable Public find on Primary CTA in Strapi (GET /api/primary-cta returned 403), or set VITE_CMS_API_TOKEN in .env.'
          )
        } else if (!data.hero.title?.trim() && data.heroCmsHttpStatus === 400) {
          setCmsError(
            'Hero title unavailable: GET /api/primary-cta returned 400. Check Strapi Primary CTA fields and remove invalid populate params.'
          )
        } else if (!data.hero.title?.trim() && data.heroCmsHttpStatus) {
          setCmsError(`Hero title unavailable: primary-cta request failed (HTTP ${data.heroCmsHttpStatus}).`)
        } else {
          setCmsError(null)
        }
      } catch (error) {
        setCmsError(error instanceof Error ? error.message : 'Failed to load CMS content')
      } finally {
        setCmsLoading(false)
      }
    }

    void loadContent()
  }, [])

  // Handle hash navigation
  useEffect(() => {
    if (hash) {
      // Small timeout to ensure DOM is ready and layout is settled
      const timeoutId = setTimeout(() => {
        const id = hash.replace('#', '')
        const element = document.getElementById(id)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [hash])

  const defaultBusinessItems = [
    {
      number: '01',
      icon: '/images/tags-stack%201.svg',
      title: 'Transparent Pricing, No Surprises',
      description:
        'We eliminate the biggest fear in digital projects: unpredictable costs. You get a clear, upfront proposal with a detailed project roadmap, so you know exactly what to expect.',
    },
    {
      number: '02',
      icon: '/images/sparkle-3%201.svg',
      title: 'Start with What You Need Today',
      description:
        'No need for a massive, disruptive overhaul. Our modular solutions (ERP, AI, Web) work independently to solve your most urgent problem first, delivering immediate value and ROI.',
    },
    {
      number: '03',
      icon: '/images/scale-up%201.svg',
      title: "Scale When You're Ready",
      description:
        "Every solution we build is designed for future integration. When it's time to grow, your systems are ready to connect seamlessly, creating a unified platform for long-term success.",
    },
    {
      number: '04',
      icon: '/images/pin-heart%201.svg',
      title: 'Local Expertise for the MENA Market',
      description:
        'We are based in the heart of the GCC. We understand the regional business landscape, compliance requirements, and market dynamics, ensuring your solution is perfectly aligned with your environment.',
    },
  ]

  useEffect(() => {
    const updateScale = () => {
      const viewportWidth = window.innerWidth
      const isMobileWidth = viewportWidth < 768
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isMobileView = isMobileWidth || isMobileUA
      setIsMobile(isMobileView)

      if (isMobileView) {
        setScale(1)
      } else {
        const desktopDesignWidth = 1920
        const newScale = Math.min(viewportWidth / desktopDesignWidth, 1)
        setScale(newScale)
      }
    }

    updateScale()
    window.addEventListener('resize', updateScale)

    return () => {
      window.removeEventListener('resize', updateScale)
    }
  }, [])

  useEffect(() => {
    if (isMobile) return

    const mainElement = mainRef.current
    if (!mainElement) return

    const updateHeight = () => {
      setContentHeight(mainElement.scrollHeight)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(mainElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isMobile, cmsData, cmsLoading])

  const cmsReady = !cmsLoading
  const hero = cmsData?.hero
  const businessItems = cmsData?.business?.items?.length
    ? cmsData.business.items.map((item, index) => ({
      number: String(index + 1).padStart(2, '0'),
      icon: item.icon,
      title: item.title,
      description: item.description,
    }))
    : cmsListOrFallback(undefined, defaultBusinessItems, cmsReady)
  const partnerItems = cmsData?.partners?.length
    ? cmsData.partners.map((p) => ({ id: p.id, name: p.name, logo: p.logo, order: p.order }))
    : cmsListOrFallback(undefined, PARTNERS, cmsReady).map((p, index) => ({
        id: index + 1,
        name: p.name,
        logo: p.logo,
        order: index + 1,
      }))

  const homeSeo = useMemo(() => {
    const resolved = resolveSeo({
      cmsSeo: cmsData?.metadata?.seo,
      legacyTitle: cmsData?.metadata?.seoTitle,
      legacyDescription: cmsData?.metadata?.seoDescription,
      pathname: '/',
      favicon: cmsData?.metadata?.favicon,
    })

    return {
      ...resolved,
      jsonLd: buildHomeJsonLd({
        organization: cmsData?.organization ?? null,
        faqs: cmsData?.faqs ?? [],
        contact: cmsData?.contact,
        services: cmsData?.services?.items,
      }),
    }
  }, [
    cmsData?.metadata,
    cmsData?.organization,
    cmsData?.faqs,
    cmsData?.contact,
    cmsData?.services?.items,
  ])

  // Removed section-level observer for Business section
  useEffect(() => {
    // Keep any other mobile logic if needed, or remove if specific to the old animation
  }, [isMobile])



  useEffect(() => {
    if (!isMobile) return
    const container = mobileCardsContainerRef.current
    if (!container) return

    const measure = () => {
      const cards = Array.from(container.children) as HTMLElement[]
      const tallest = cards.reduce((max, card) => {
        card.style.minHeight = ''
        return Math.max(max, card.getBoundingClientRect().height)
      }, 0)
      if (tallest > 0) setMobileCardHeight(tallest)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [isMobile, businessItems])

  // If mobile, render mobile version without scaling (consistent with ServicePage)
  if (isMobile) {
    return (
      <>
        <SEOHead {...homeSeo} />
        <div style={{ position: 'relative', width: '100vw', maxWidth: '100%', margin: '0 auto', background: 'white' }}>
          {cmsError && (
            <div style={{ padding: '8px 16px', background: '#ffe5e5', color: '#b00020', fontSize: '12px' }}>
              CMS unavailable. Showing fallback content.
            </div>
          )}
          <Header />
          <Hero
            isMobile={true}
            title={hero?.title}
            cmsReady={cmsReady}
            backgroundImage={hero?.backgroundImage}
          />
          <section
            id="business"
            ref={businessRef}
            aria-label="Business section"
            style={{
              position: 'relative',
              width: '100%',
              minHeight: 'auto',
              padding: '0 0 50px 0',
              marginTop: '-2px',
              overflow: 'visible',
            }}
          >
            {/* Background Gradient */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                bottom: '0',
                left: '0',
                top: '-64px',
                background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 2.08%, #000000 15.06%)',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />

            {/* Content matching CSS Section 2 */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {/* Logos */}
              <PartnerSlotGrid
                partners={partnerItems}
                mobile
                style={{
                  width: '371px',
                  maxWidth: 'calc(100% - 32px)',
                  height: '31px',
                  margin: '10px auto 50px',
                }}
              />

              {/* Headline */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '364px',
                  maxWidth: 'calc(100% - 32px)',
                  gap: '8px',
                  marginBottom: '50px',
                }}
              >
                <h2
                  title={cmsTextTitle(cmsData?.business?.title)}
                  style={{
                    fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                    fontWeight: 600,
                    fontSize: '22px',
                    lineHeight: '27px',
                    textAlign: 'center',
                    color: '#FFFFFF',
                    margin: 0,
                    width: '100%',
                    ...cmsClamp(2),
                  }}
                >
                  {cmsTextOrFallback(cmsData?.business?.title, BUSINESS_TITLE_FALLBACK, cmsReady)}
                </h2>
                <p
                  title={cmsTextTitle(cmsData?.business?.description)}
                  style={{
                    fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                    fontWeight: 400,
                    fontSize: '17px',
                    lineHeight: '21px',
                    textAlign: 'center',
                    color: '#FFFFFF',
                    margin: 0,
                    width: '100%',
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                  }}
                >
                  {cmsTextOrFallback(cmsData?.business?.description, BUSINESS_DESC_FALLBACK, cmsReady)}
                </p>
              </div>

              {/* Cards */}
              <div
                ref={mobileCardsContainerRef}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '24px',
                  width: '100%',
                }}
              >
                {businessItems.map((item) => (
                  <div
                    key={item.number}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: '16px',
                      gap: '12px',
                      width: '370px',
                      maxWidth: 'calc(100% - 32px)',
                      minHeight: mobileCardHeight,
                      background: 'linear-gradient(119.4deg, rgba(231, 240, 255, 0.08) 21.01%, rgba(14, 31, 161, 0.08) 160.62%)',
                      backgroundBlendMode: 'hard-light',
                      borderRadius: '24px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Number */}
                    <div
                      style={{
                        fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                        fontWeight: 400,
                        fontSize: '22px',
                        lineHeight: '27px',
                        color: 'rgba(255, 255, 255, 0.52)',
                      }}
                    >
                      {item.number}
                    </div>

                    {/* Icon */}
                    <div
                      style={{
                        width: '54px',
                        height: '54px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img src={item.icon} alt={item.title} style={{ width: '100%', height: '100%' }} />
                    </div>

                    {/* Content */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '8px',
                        width: '100%',
                      }}
                    >
                      <h3
                        title={item.title}
                        style={{
                          width: '100%',
                          fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                          fontWeight: 400,
                          fontSize: '18px',
                          lineHeight: '22px',
                          color: '#FFFFFF',
                          margin: 0,
                          overflowWrap: 'break-word',
                          wordBreak: 'normal',
                        }}
                      >
                        {item.title}
                      </h3>
                      <p
                        title={item.description}
                        style={{
                          width: '100%',
                          fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                          fontWeight: 400,
                          fontSize: '17px',
                          lineHeight: '21px',
                          color: 'rgba(255, 255, 255, 0.52)',
                          margin: 0,
                          overflowWrap: 'break-word',
                          wordBreak: 'normal',
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <Process
            isMobile={true}
            cmsReady={cmsReady}
            title={cmsData?.clearPath?.title}
            description={cmsData?.clearPath?.description}
            steps={cmsData?.clearPath?.steps}
          />
          <div style={{ marginBottom: '20px' }}>
            <Services
              isMobile={true}
              cmsReady={cmsReady}
              title={cmsData?.services?.title}
              description={cmsData?.services?.description}
              items={cmsData?.services?.items}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <About
              isMobile={true}
              cmsReady={cmsReady}
              title={cmsData?.about?.title}
              description={cmsData?.about?.description}
              collageBySlot={cmsData?.about?.collageBySlot}
              footerStats={cmsData?.about?.footerStats}
            />
          </div>
          {/* <Testimonials isMobile={true} /> */}
          <Contact isMobile={true} cmsReady={cmsReady} contactInfo={cmsData?.contact} />
          <Footer
            isMobile={true}
            cmsReady={cmsReady}
            contactInfo={cmsData?.contact}
            serviceItems={cmsData?.services?.items}
          />
        </div>

        {ENABLE_AI_CHAT && <FloatingAIChat isMobile={true} />}
      </>
    )
  }

  // Desktop version with scaling
  return (
    <>
      <SEOHead {...homeSeo} />
      <div
        className="w-screen overflow-x-hidden flex justify-center bg-white"
        style={{
          minHeight: '100vh'
        }}
      >
        {cmsLoading && (
          <div style={{ position: 'fixed', top: '8px', right: '8px', zIndex: 2000, fontSize: '12px', color: '#666' }}>
            Loading CMS...
          </div>
        )}
        {cmsError && (
          <div
            style={{
              position: 'fixed',
              top: cmsLoading ? '28px' : '8px',
              right: '8px',
              left: '8px',
              zIndex: 2000,
              padding: '8px 16px',
              background: '#ffe5e5',
              color: '#b00020',
              fontSize: '12px',
              borderRadius: '8px',
            }}
          >
            {cmsError}
          </div>
        )}
        <main
          ref={mainRef}
          className="relative bg-white"
          style={{
            width: '1920px',
            transformOrigin: 'top center',
            transform: `scale(${scale})`,
            marginBottom: contentHeight > 0 ? `-${contentHeight * (1 - scale)}px` : undefined,
          }}
          role="main"
          aria-label="Main content"
        >
          <Header />
          <Hero
            isMobile={false}
            title={hero?.title}
            cmsReady={cmsReady}
            backgroundImage={hero?.backgroundImage}
          />
          <section
            id="business"
            ref={businessRef}
            aria-label="Business section"
            className="business-section"
            style={{
              position: 'relative',
              width: '1920px',
              height: '1700px', // Adjusted for 77% scaled content (original 2171px * 0.85 ≈ 1850px)
              margin: '0 auto',
              background: '#000000',
              overflow: 'visible',
              zIndex: 5,
            }}
          >
            {/* Rectangle 67 - Background Gradient */}
            <div
              style={{
                position: 'absolute',
                width: '1917px',
                height: '1213px', // Scaled to 77% (1575 * 0.77 ≈ 1213)
                left: '3px',
                top: '-289px', // Scaled to 77% (-375 * 0.77 ≈ -289)
                background: 'linear-gradient(180deg, rgba(14, 29, 39, 0) 0%, #07141D 21.59%, #000000 45%)',
                zIndex: -1,
                pointerEvents: 'none',
              }}
            />

            {/* Partners Band - Frame 7971 */}
            <AnimatedPartnersBand partners={partnerItems} />

            <AnimatedTitle title={cmsData?.business?.title} cmsReady={cmsReady} />
            <AnimatedDescription description={cmsData?.business?.description} cmsReady={cmsReady} />

            {/* Business Items Container - Frame 1000005146 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                position: 'absolute',
                width: '1295px', // Scaled to 77% (1682 * 0.77 ≈ 1295)
                height: '1192px', // Scaled to 77% (1548 * 0.77 ≈ 1192)
                left: '313px', // Centered: (1920 - 1295) / 2 ≈ 313
                top: '520px',
              }}
            >
              {businessItems.map((item, index) => (
                <BusinessItem key={item.number} item={item} index={index} />
              ))}
            </div>
          </section>
          <Process
            isMobile={false}
            cmsReady={cmsReady}
            title={cmsData?.clearPath?.title}
            description={cmsData?.clearPath?.description}
            steps={cmsData?.clearPath?.steps}
          />
          <Services
            isMobile={false}
            cmsReady={cmsReady}
            title={cmsData?.services?.title}
            description={cmsData?.services?.description}
            items={cmsData?.services?.items}
          />
          <About
            isMobile={false}
            cmsReady={cmsReady}
            title={cmsData?.about?.title}
            description={cmsData?.about?.description}
            collageBySlot={cmsData?.about?.collageBySlot}
            footerStats={cmsData?.about?.footerStats}
          />
          {/* <Testimonials isMobile={false} /> */}
          <Contact isMobile={false} cmsReady={cmsReady} contactInfo={cmsData?.contact} />
          <Footer
            isMobile={false}
            cmsReady={cmsReady}
            contactInfo={cmsData?.contact}
            serviceItems={cmsData?.services?.items}
          />
        </main>
      </div>

      {ENABLE_AI_CHAT && <FloatingAIChat isMobile={false} scale={scale} />}
    </>
  )
}

const PARTNER_BAND_SLOTS = 6

function PartnerSlotGrid({
  partners,
  slotCount = PARTNER_BAND_SLOTS,
  mobile = false,
  style,
  className,
}: {
  partners: ReadonlyArray<{ id?: number; name: string; logo: string; order?: number }>
  slotCount?: number
  mobile?: boolean
  style?: CSSProperties
  className?: string
}) {
  const byOrder = new Map<number, (typeof partners)[number]>()
  const unplaced: (typeof partners)[number][] = []

  for (const partner of partners) {
    const order = partner.order ?? 0
    if (order >= 1 && order <= slotCount && !byOrder.has(order)) {
      byOrder.set(order, partner)
    } else {
      unplaced.push(partner)
    }
  }

  for (const partner of unplaced) {
    for (let slot = 1; slot <= slotCount; slot += 1) {
      if (!byOrder.has(slot)) {
        byOrder.set(slot, partner)
        break
      }
    }
  }

  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${slotCount}, minmax(0, 1fr))`,
        alignItems: 'center',
        justifyItems: 'center',
        ...style,
      }}
    >
      {Array.from({ length: slotCount }, (_, index) => {
        const partner = byOrder.get(index + 1)
        return (
          <div
            key={`partner-slot-${index + 1}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            {partner ? (
              <img
                src={partner.logo}
                alt={partner.name}
                style={{
                  display: 'block',
                  filter: 'brightness(0) invert(1)',
                  maxHeight: mobile ? '31px' : '71px',
                  width: 'auto',
                  maxWidth: '100%',
                }}
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

// Sub-components for Animations
function AnimatedPartnersBand({
  partners,
}: {
  partners: ReadonlyArray<{ id?: number; name: string; logo: string; order?: number }>
}) {
  const { ref, isVisible } = useInView()
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{
        position: 'absolute',
        width: '1273px',
        height: '71.05px',
        left: '324px',
        top: '9px',
        zIndex: 10,
      }}
    >
      <PartnerSlotGrid
        partners={partners}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

function AnimatedTitle({ title, cmsReady = true }: { title?: string; cmsReady?: boolean }) {
  const { ref, isVisible } = useInView()
  const displayTitle = cmsTextOrFallback(title, BUSINESS_TITLE_FALLBACK, cmsReady)

  return (
    <h2
      ref={ref}
      title={cmsTextTitle(displayTitle)}
      className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{
        position: 'absolute',
        width: '1200px',
        height: '59px',
        left: 'calc(50% - 1200px/2)',
        top: '240px',
        fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
        fontWeight: 600,
        fontSize: '48px',
        lineHeight: '59px',
        textAlign: 'center',
        color: '#FFFFFF',
        margin: 0,
        boxSizing: 'border-box',
        ...cmsClamp(1),
      }}
    >
      {displayTitle}
    </h2>
  )
}

function AnimatedDescription({ description, cmsReady = true }: { description?: string; cmsReady?: boolean }) {
  const { ref, isVisible } = useInView()
  const displayDescription = cmsTextOrFallback(description, BUSINESS_DESC_FALLBACK, cmsReady)

  return (
    <p
      ref={ref}
      title={cmsTextTitle(displayDescription)}
      className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{
        position: 'absolute',
        width: '1200px',
        maxWidth: 'calc(100% - 48px)',
        left: 'calc(50% - 600px)',
        top: '320px',
        fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '34px',
        textAlign: 'center',
        color: '#FFFFFF',
        margin: 0,
        transitionDelay: '100ms',
        boxSizing: 'border-box',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
      }}
    >
      {displayDescription}
    </p>
  )
}

const BUSINESS_ITEM_TEXT_COL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '18px',
  width: '662px',
  flexShrink: 0,
  overflow: 'hidden',
}

function BusinessItem({ item, index }: { item: any, index: number }) {
  const { ref, isVisible } = useInView()
  const textColHeight = index === 3 ? '165px' : '139px'
  const descHeight = index === 3 ? '105px' : '79px'
  const descLineClamp = index === 3 ? 4 : 3

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        width: '1295px', // Scaled to 77% (1682px * 0.77 ≈ 1295px)
        paddingBottom: index < 3 ? '0px' : '0px',
      }}
      className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '79px 25px', // Scaled to 77% (102px * 0.77 ≈ 79px, 32px * 0.77 ≈ 25px)
          gap: '8px', // Scaled to 77% (10px * 0.77 ≈ 8px)
          width: '100%',
          height: '298px', // Scaled to 77% (387px * 0.77 ≈ 298px)
          //background: index === 0 ? 'linear-gradient(93.18deg, rgba(217, 217, 217, 0.04) 2.63%, rgba(14, 31, 161, 0.04) 162.78%)' : 'transparent',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px',
            gap: '220px', // Increased from 182px (237 * 0.77) to add more space between SVG and text
            width: '1245px', // Scaled to 77% (1618px * 0.77 ≈ 1245px)
            height: index === 3 ? '165px' : '139px', // Scaled to 77% (214px * 0.77 ≈ 165px, 180px * 0.77 ≈ 139px)
          }}
        >
          {/* Number */}
          <div
            style={{
              width: '96px', // Scaled to 77% (125px * 0.77 ≈ 96px)
              height: '45px', // Scaled to 77% (59px * 0.77 ≈ 45px)
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 400,
              fontSize: '37px', // Scaled to 77% (48px * 0.77 ≈ 37px)
              lineHeight: '45px', // Scaled to 77% (59px * 0.77 ≈ 45px)
              color: 'rgba(255, 255, 255, 0.52)',
              textAlign: 'center',
            }}
          >
            {item.number}
          </div>

          {/* Icon */}
          <div
            style={{
              width: '128px', // Scaled to 77% (166px * 0.77 ≈ 128px)
              height: '128px', // Scaled to 77% (166px * 0.77 ≈ 128px)
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <img src={item.icon} alt={item.title} style={{ width: '100%', height: 'auto' }} />
          </div>

          {/* Text Content — fixed Figma box; CMS text clipped inside, never breaks layout */}
          <div style={{ ...BUSINESS_ITEM_TEXT_COL, height: textColHeight }}>
            <h3
              title={item.title}
              style={{
                width: '662px',
                height: '42px',
                fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                fontWeight: 400,
                fontSize: '34px',
                lineHeight: '42px',
                color: '#FFFFFF',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.title}
            </h3>
            <p
              title={item.description}
              style={{
                width: '662px',
                height: descHeight,
                fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
                fontWeight: 400,
                fontSize: '22px',
                lineHeight: '26px',
                color: 'rgba(255, 255, 255, 0.52)',
                margin: 0,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: descLineClamp,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.description}
            </p>
          </div>
        </div>
      </div>
      {/* Line separator - 75% of viewport width (1920px * 0.75 = 1440px) */}
      {index < 3 && (
        <div
          style={{
            width: '1440px', // 75% of 1920px viewport
            height: '1px',
            background: 'rgba(255, 255, 255, 0.44)',
          }}
        />
      )}
    </div>
  )
}

